// TODO-SERGEY: the types are duplicated in the client and server code. We need to make them common after properly moving to NX.
import { z } from 'zod'

export enum AnalyticsProvider {
	META = 'meta',
	MIXPANEL = 'mixpanel',
}

export enum AnalyticsDevice {
	WEB_BROWSER = 'web-browser',
	MOBILE_APPLICATION = 'mobile-application',
}

export enum ActionName {
	PURCHASE = 'purchase',
	VIEW_ITEM_LIST = 'viewItemList',
	VIEW_ITEM = 'viewItem',
	SELECT_ITEM = 'selectItem',
	ADD_TO_CART = 'addToCart',
	REMOVE_FROM_CART = 'removeFromCart',
	VIEW_CART = 'viewCart',
	BEGIN_CHECKOUT = 'beginCheckout',
	ADD_PAYMENT_INFO = 'addPaymentInfo',
	ADD_SHIPPING_INFO = 'addShippingInfo',
	PAGE_VIEW = 'pageView',
	SELECT_PROMOTION = 'selectPromotion',
	VIEW_PROMOTION = 'viewPromotion',
}

export enum ActionDataKey {
	SHIPPING_TIER = 'shipping-tier', // content type
	QUANTITY = 'quantity',
	AFFILIATION = 'affiliation',
	IS_FIRST_PURCHASE = 'is-first-purchase',
	PRODUCT_ITEMS = 'product-items',
	PRODUCT_ITEM_IDS = 'product-item-ids',
	NUMBER_OF_ITEMS = 'number-of-items',
	CHECKOUT_STEP = 'checkout-step',
	COUPON = 'coupon',
	COUPONS = 'coupons',

	USER_EMAIL = 'user-email',
	USER_FIRST_NAME = 'user-first-name',
	USER_LAST_NAME = 'user-last-name',
	USER_PHONE_NUMBER = 'user-phone-number',
	USER_STATUS = 'user-status', // signed in / quest

	ORDER_NUMBER = 'id',
	TICTUK_ORDER_ID = 'tictuk-order-id',

	BRAND = 'brand',
	LOCALE = 'locale',
	CURRENCY = 'currency',
	COUNTRY_CODE = 'country-code',
	STORE_NAME = 'store-name',
	STORE_ID = 'store-id',

	PAYMENT_TYPE = 'payment-type',
	REVENUE = 'revenue',
	TAX = 'tax',
	DELIVERY_CHARGE = 'delivery-charge',
	CHARGES = 'charges',

	ITEM_NAME = 'item-name',
	ITEM_ID = 'item-id',
	ITEM_LIST_NAME = 'item-list-name',
	ITEM_QUANTITY = 'item-quantity',
	ITEM_SECTION_TITLE = 'item-section-title',
}

export enum ActionDataTransformation {
	REGEX = 'regex',
	JSON_STRINGIFY = 'jsonStringify',
}

export const transformationsSchema = z.array(
	z.object({
		name: z.nativeEnum(ActionDataTransformation),
		arguments: z.array(z.union([z.string(), z.number()])),
	})
)

export type Transformations = z.infer<typeof transformationsSchema>

export const eventConfigurationSchema = z.array(
	z.object({
		attributeName: z.string(),
		dataKey: z.nativeEnum(ActionDataKey).or(z.string()),
		transformations: transformationsSchema,
	})
)
export type EventConfiguration = z.infer<typeof eventConfigurationSchema>

export const actionConfigurationSchema = z.record(z.string(), eventConfigurationSchema)
export type ActionConfiguration = z.infer<typeof actionConfigurationSchema>

export const providerSchema = z.object({
	name: z.nativeEnum(AnalyticsProvider),
	origin: z.array(z.nativeEnum(AnalyticsDevice)).optional(),
	actions: z.record(z.nativeEnum(ActionName), actionConfigurationSchema),
})
export type ProviderConfiguration = z.infer<typeof providerSchema>

export const configurationSchema = z.object({
	providers: z.array(providerSchema),
})
export type Configuration = z.infer<typeof configurationSchema>

export const actionDataSchemas = {
	[ActionName.PURCHASE]: z.object({
		[ActionDataKey.ORDER_NUMBER]: z.string(),
		[ActionDataKey.TICTUK_ORDER_ID]: z.number(),
		[ActionDataKey.SHIPPING_TIER]: z.string(),
		[ActionDataKey.REVENUE]: z.number(),
		[ActionDataKey.TAX]: z.string(),
		[ActionDataKey.DELIVERY_CHARGE]: z.string(),
		[ActionDataKey.IS_FIRST_PURCHASE]: z.string(),
		[ActionDataKey.CURRENCY]: z.string(),
		[ActionDataKey.PAYMENT_TYPE]: z.string(),
		[ActionDataKey.AFFILIATION]: z.string(),
		[ActionDataKey.USER_EMAIL]: z.string(),
		[ActionDataKey.USER_FIRST_NAME]: z.string(),
		[ActionDataKey.USER_LAST_NAME]: z.string(),
		[ActionDataKey.USER_PHONE_NUMBER]: z.string(),
		[ActionDataKey.USER_STATUS]: z.string(),
		[ActionDataKey.COUPON]: z.string(),
	}),
	[ActionName.VIEW_ITEM]: z.object({
		[ActionDataKey.CURRENCY]: z.string(),
		[ActionDataKey.ITEM_SECTION_TITLE]: z.string(),
		[ActionDataKey.ITEM_ID]: z.string(),
		[ActionDataKey.REVENUE]: z.number(),
	}),
	[ActionName.ADD_TO_CART]: z.object({
		[ActionDataKey.PRODUCT_ITEMS]: z.any(),
		[ActionDataKey.REVENUE]: z.number(),
		[ActionDataKey.CHARGES]: z.string(),
		[ActionDataKey.COUPONS]: z.string(),
		[ActionDataKey.CURRENCY]: z.string(),
	}),
	[ActionName.BEGIN_CHECKOUT]: z.object({
		[ActionDataKey.COUNTRY_CODE]: z.string(),
		[ActionDataKey.REVENUE]: z.number(),
		[ActionDataKey.STORE_NAME]: z.string(),
		[ActionDataKey.STORE_ID]: z.string(),
		[ActionDataKey.CHECKOUT_STEP]: z.string(),
		[ActionDataKey.SHIPPING_TIER]: z.string(),
		[ActionDataKey.CURRENCY]: z.string(),
		[ActionDataKey.PRODUCT_ITEM_IDS]: z.array(z.string()),
		[ActionDataKey.NUMBER_OF_ITEMS]: z.number(),
	}),
	[ActionName.ADD_PAYMENT_INFO]: z.object({
		[ActionDataKey.LOCALE]: z.string(),
		[ActionDataKey.COUNTRY_CODE]: z.string(),
		[ActionDataKey.REVENUE]: z.number(),
		[ActionDataKey.STORE_NAME]: z.string(),
		[ActionDataKey.STORE_ID]: z.string(),
		[ActionDataKey.CHECKOUT_STEP]: z.string(),
		[ActionDataKey.CHARGES]: z.string(),
		[ActionDataKey.COUPONS]: z.string(),
		[ActionDataKey.CURRENCY]: z.string(),
		[ActionDataKey.NUMBER_OF_ITEMS]: z.number(),
	}),
}

export type PurchaseAction = z.infer<typeof actionDataSchemas[ActionName.PURCHASE]>
export type ViewItemAction = z.infer<typeof actionDataSchemas[ActionName.VIEW_ITEM]>
export type AddToCartAction = z.infer<typeof actionDataSchemas[ActionName.ADD_TO_CART]>
export type BeginCheckoutAction = z.infer<typeof actionDataSchemas[ActionName.BEGIN_CHECKOUT]>
export type AddPaymentInfoAction = z.infer<typeof actionDataSchemas[ActionName.ADD_PAYMENT_INFO]>

export type Action = AddPaymentInfoAction | BeginCheckoutAction | AddToCartAction | ViewItemAction | PurchaseAction
